import { Copy, Reply } from "lucide-react";
import { animate, motion, useMotionValue } from "motion/react";
import { createContext, type ReactNode, use, useEffect, useRef, useState } from "react";
import { getAvailableViewportBounds } from "../../../../hooks/useViewportAwarePlacement";
import { cn } from "../../../../lib/cn";
import { Line } from "../../../Line";
import { Pressable } from "../../../Pressable";
import { Label } from "../../../typography/Label";
import { useChat } from "../../ChatContext";
import { formatChatLabel } from "../../labels";
import { getMessagePlainText, getReplyPreviewText } from "../Content";
import { useMessage } from "../Context";
import { CHAT_REACTIONS, type ChatReaction } from "../types";

const REACTION_BAR_SLOT_PX = 56;
const ACTION_MENU_SLOT_PX = 112;
const EDGE_PADDING_PX = 48;
const BACKDROP_EASE = [0.23, 1, 0.32, 1] as const;
const MESSAGE_EASE = [0.32, 0.72, 0, 1] as const;
const OPENING_GESTURE_GUARD_MS = 450;

type MessageOriginRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

type MessageSelectionState = {
    selectedMessageId: string | null;
    originRect: MessageOriginRect | null;
};

type MessageSelectionActions = {
    select: (messageId: string, originRect: MessageOriginRect) => void;
    dismiss: () => void;
};

type MessageSelectionContextValue = {
    state: MessageSelectionState;
    actions: MessageSelectionActions;
};

const MessageSelectionContext = createContext<MessageSelectionContextValue | null>(
    null,
);

type MessageSelectionProviderProps = {
    children: ReactNode;
};

export function MessageSelectionProvider({ children }: MessageSelectionProviderProps) {
    const [state, setState] = useState<MessageSelectionState>({
        selectedMessageId: null,
        originRect: null,
    });

    const actions: MessageSelectionActions = {
        select(messageId, originRect) {
            setState({ selectedMessageId: messageId, originRect });
        },
        dismiss() {
            setState({ selectedMessageId: null, originRect: null });
        },
    };

    return (
        <MessageSelectionContext value={{ state, actions }}>
            {children}
        </MessageSelectionContext>
    );
}

export function useMessageSelection() {
    const selection = use(MessageSelectionContext);

    if (!selection) {
        throw new Error(
            "Message selection must be used within MessageSelectionProvider",
        );
    }

    return selection;
}

type SelectedMessageOverlayProps = {
    children: ReactNode;
    originRect: MessageOriginRect;
    onDismiss: () => void;
};

export function SelectedMessageOverlay({
    children,
    originRect,
    onDismiss,
}: SelectedMessageOverlayProps) {
    const { sender } = useMessage();
    const { labels } = useChat();
    const targetTop = getTargetTop(originRect);
    const top = useMotionValue(originRect.top);
    const backdropOpacity = useMotionValue(0);
    const isDismissingRef = useRef(false);
    const canDismissRef = useRef(false);
    const [isChromeVisible, setIsChromeVisible] = useState(true);
    const alignsEnd = sender === "member";

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        void animate(backdropOpacity, 1, {
            duration: 0.2,
            ease: BACKDROP_EASE,
        });
        void animate(top, targetTop, {
            duration: 0.28,
            ease: MESSAGE_EASE,
        });

        function swallowOpeningGestureClick(event: MouseEvent) {
            event.preventDefault();
            event.stopPropagation();
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        }

        document.addEventListener("click", swallowOpeningGestureClick, true);

        const guardTimeoutId = window.setTimeout(() => {
            canDismissRef.current = true;
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        }, OPENING_GESTURE_GUARD_MS);

        return () => {
            document.body.style.overflow = previousOverflow;
            canDismissRef.current = false;
            window.clearTimeout(guardTimeoutId);
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        };
    }, [backdropOpacity, targetTop, top]);

    async function handleDismiss() {
        if (!canDismissRef.current || isDismissingRef.current) {
            return;
        }

        isDismissingRef.current = true;
        setIsChromeVisible(false);

        await Promise.all([
            animate(backdropOpacity, 0, {
                duration: 0.18,
                ease: BACKDROP_EASE,
            }),
            animate(top, originRect.top, {
                duration: 0.24,
                ease: MESSAGE_EASE,
            }),
        ]);

        onDismiss();
    }

    return (
        <div className="fixed inset-0 z-[500]">
            <motion.button
                type="button"
                aria-label={labels.dismissSelection}
                className="absolute inset-0 bg-background-chat/70 backdrop-blur-md"
                style={{ opacity: backdropOpacity }}
                onClick={handleDismiss}
            />
            <motion.div
                className="pointer-events-none absolute"
                style={{
                    top,
                    left: originRect.left,
                    width: originRect.width,
                }}
            >
                <div className="pointer-events-auto relative w-full">
                    {isChromeVisible ? (
                        <motion.div
                            className={cn(
                                "absolute bottom-full mb-8 flex",
                                alignsEnd ? "right-0" : "left-32",
                            )}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2, ease: BACKDROP_EASE }}
                        >
                            <ReactionBar onDismiss={handleDismiss} />
                        </motion.div>
                    ) : null}

                    {children}

                    {isChromeVisible ? (
                        <motion.div
                            className={cn(
                                "absolute top-full mt-8 flex",
                                alignsEnd ? "right-0" : "left-32",
                            )}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                                duration: 0.2,
                                ease: BACKDROP_EASE,
                                delay: 0.04,
                            }}
                        >
                            <ActionMenu onDismiss={handleDismiss} />
                        </motion.div>
                    ) : null}
                </div>
            </motion.div>
        </div>
    );
}

export function ReactionBar({
    onDismiss,
    className,
}: {
    onDismiss: () => void;
    className?: string;
}) {
    const { reaction: activeReaction, onReact, onRemoveReaction } = useMessage();
    const { labels } = useChat();

    function toggle(reaction: ChatReaction) {
        if (reaction === activeReaction) {
            onRemoveReaction?.();
        } else {
            onReact?.(reaction);
        }

        onDismiss();
    }

    return (
        <div
            className={cn(
                "flex items-center gap-4 rounded-full bg-surface-default px-8 py-4",
                className,
            )}
        >
            {CHAT_REACTIONS.map((reaction) => {
                const isActive = reaction === activeReaction;

                return (
                    <Pressable
                        key={reaction}
                        aria-label={
                            isActive
                                ? formatChatLabel(labels.removeReaction, { reaction })
                                : formatChatLabel(labels.reactWith, { reaction })
                        }
                        aria-pressed={isActive}
                        className={cn(
                            "grid size-40 place-items-center rounded-full text-[22px] leading-none transition-transform duration-150 ease-out active:scale-[0.92]",
                            isActive && "bg-component-chat-agent",
                        )}
                        onPress={() => toggle(reaction)}
                    >
                        {reaction}
                    </Pressable>
                );
            })}
        </div>
    );
}

export function ActionMenu({
    onDismiss,
    className,
}: {
    onDismiss: () => void;
    className?: string;
}) {
    const { message, messageHtml, attachments, onReply, onCopy } = useMessage();
    const { labels } = useChat();

    function handleReply() {
        onReply?.(
            getReplyPreviewText({
                message,
                messageHtml,
                attachments,
                imageLabel: labels.replyImage,
                audioLabel: labels.replyAudio,
                fileFallbackLabel: labels.fileAttachmentFallback,
            }),
        );
        onDismiss();
    }

    async function handleCopy() {
        const text = getMessagePlainText(message, messageHtml);
        if (text) {
            await navigator.clipboard.writeText(text);
            onCopy?.();
        }
        onDismiss();
    }

    return (
        <div
            className={cn(
                "min-w-[180px] overflow-hidden rounded-rounded bg-surface-default",
                className,
            )}
        >
            <Pressable
                className="flex w-full items-center justify-between gap-24 px-16 py-12 transition-transform duration-150 ease-out active:scale-[0.98]"
                onPress={handleReply}
            >
                <Label size="medium" weight="medium-bold">
                    {labels.reply}
                </Label>
                <Reply aria-hidden className="size-24 text-text-primary" />
            </Pressable>

            <Line className="border-border-subtle" />

            <Pressable
                className="flex w-full items-center justify-between gap-24 px-16 py-12 transition-transform duration-150 ease-out active:scale-[0.98]"
                onPress={handleCopy}
            >
                <Label size="medium" weight="medium-bold">
                    {labels.copy}
                </Label>
                <Copy aria-hidden className="size-24 text-text-primary" />
            </Pressable>
        </div>
    );
}

function getTargetTop(originRect: MessageOriginRect): number {
    const bounds = getAvailableViewportBounds(
        document.documentElement,
        EDGE_PADDING_PX,
    );
    const minTop = bounds.top + REACTION_BAR_SLOT_PX;
    const maxTop = bounds.bottom - ACTION_MENU_SLOT_PX - originRect.height;

    if (maxTop < minTop) {
        const clusterHeight =
            REACTION_BAR_SLOT_PX + originRect.height + ACTION_MENU_SLOT_PX;
        return (
            bounds.top +
            (bounds.bottom - bounds.top - clusterHeight) / 2 +
            REACTION_BAR_SLOT_PX
        );
    }

    return Math.min(Math.max(originRect.top, minTop), maxTop);
}
